import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrandingConfig } from '../utils/siteConfig';
import { submitContactMessage } from '../utils/secureFunctions';
import BrandLogo from '../components/BrandLogo';
import './Landing.css';

export default function Landing() {
  const navigate = useNavigate();
  const { config } = useBrandingConfig('published');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  // UI interaction state
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [pressedButton, setPressedButton] = useState<string | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [heroHover, setHeroHover] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // trigger entrance animations after mount
    const t = setTimeout(() => setPageLoaded(true), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);

  const navItems: Array<{ id: string; label: string; href: string }> = [
    { id: 'features', label: 'Features', href: '#features' },
    { id: 'about', label: 'About', href: '#about' },
    { id: 'contact', label: 'Contact', href: '#contact' },
  ];

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage('');
    
    try {
      await submitContactMessage(contactForm);
      
      setSubmitMessage('Thank you! Your message has been sent. We will get back to you soon.');
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitMessage('Sorry, there was an error. Please try emailing us directly at info@belivah.com');
    } finally {
      setSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();

  const scrollToSection = (selector: string) => {
    try {
      const el = document.querySelector(selector);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {}
  };

  const handleNavLink = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      event.preventDefault();
      scrollToSection(href);
    }
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const renderNavLink = (item: { id: string; label: string; href: string }, variant: 'desktop' | 'mobile') => (
    <div
      key={`${variant}-${item.id}`}
      style={{
        ...styles.linkWrap,
        ...(variant === 'mobile' ? styles.linkWrapMobile : {})
      }}
      onMouseEnter={() => setHoveredLink(item.id)}
      onMouseLeave={() => setHoveredLink(null)}
    >
      <a
        href={item.href}
        onClick={(event) => handleNavLink(event, item.href)}
        style={{
          ...styles.link,
          ...(variant === 'mobile' ? styles.linkMobile : {}),
          color: hoveredLink === item.id ? '#66EAD1' : '#fff'
        }}
      >
        {item.label}
      </a>
      <div style={{
        ...styles.linkUnderline,
        width: hoveredLink === item.id ? '100%' : '0%'
      }} />
    </div>
  );

  return (
    <div style={styles.wrapper}>
      <nav style={{...styles.navbar, ...(isMobile ? styles.navbarMobile : {})}}>
        <div style={{...styles.logo, ...(isMobile ? { fontSize: '1.6rem' } : {})}}>
          {config.logoUrl ? (
            <img src={config.logoUrl} alt={config.brandName} style={{ height: isMobile ? 24 : 28, objectFit: 'contain' }} />
          ) : (
            <>
              <BrandLogo text={config.brandName || 'Belivah'} size={isMobile ? 30 : 38} color="inherit" />
              <span style={styles.tagline}>{config.tagline || 'INSPIRE. ENTERTAIN. CONNECT.'}</span>
            </>
          )}
        </div>
        {!isMobile && (
          <div style={styles.navLinks}>
            {navItems.map((item) => renderNavLink(item, 'desktop'))}
          </div>
        )}
        <div style={styles.navRight}>
          {!isMobile && (
            <div style={styles.authButtons}>
              <button
                style={{
                  ...styles.loginBtn,
                  ...(hoveredButton === 'login' ? styles.loginBtnHover : {}),
                  ...(pressedButton === 'login' ? styles.btnPressed : {})
                }}
                onMouseEnter={() => setHoveredButton('login')}
                onMouseLeave={() => { setHoveredButton(null); if (pressedButton === 'login') setPressedButton(null); }}
                onMouseDown={() => setPressedButton('login')}
                onMouseUp={() => setPressedButton(null)}
                onClick={() => navigate('/login')}
              >
                Log In
              </button>
              <button
                style={{
                  ...styles.signupBtn,
                  ...(hoveredButton === 'signup' ? styles.signupBtnHover : {}),
                  ...(pressedButton === 'signup' ? styles.btnPressed : {})
                }}
                onMouseEnter={() => setHoveredButton('signup')}
                onMouseLeave={() => { setHoveredButton(null); if (pressedButton === 'signup') setPressedButton(null); }}
                onMouseDown={() => setPressedButton('signup')}
                onMouseUp={() => setPressedButton(null)}
                onClick={() => navigate('/signup')}
              >
                Sign Up
              </button>
            </div>
          )}
          {isMobile && (
            <button
              style={{
                ...styles.mobileMenuButton,
                ...(mobileMenuOpen ? styles.mobileMenuButtonActive : {})
              }}
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <span
                style={{
                  ...styles.mobileMenuIconBar,
                  transform: mobileMenuOpen ? 'translateY(7px) rotate(45deg)' : 'none'
                }}
              />
              <span
                style={{
                  ...styles.mobileMenuIconBar,
                  opacity: mobileMenuOpen ? 0 : 1
                }}
              />
              <span
                style={{
                  ...styles.mobileMenuIconBar,
                  transform: mobileMenuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none'
                }}
              />
            </button>
          )}
        </div>
      </nav>
      {isMobile && (
        <>
          {mobileMenuOpen && (
            <div
              style={styles.mobileMenuBackdrop}
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
          <div style={{
            ...styles.mobileMenuPanel,
            ...(mobileMenuOpen ? styles.mobileMenuPanelOpen : {})
          }}>
            <div style={styles.mobileMenuLinks}>
              {navItems.map((item) => renderNavLink(item, 'mobile'))}
            </div>
            <div style={styles.mobileMenuActions}>
              <button
                style={{
                  ...styles.loginBtn,
                  ...styles.loginBtnMobile,
                  ...styles.mobileMenuAuthButton,
                  ...(hoveredButton === 'login-mobile' ? styles.loginBtnHover : {}),
                  ...(pressedButton === 'login-mobile' ? styles.btnPressed : {})
                }}
                onMouseEnter={() => setHoveredButton('login-mobile')}
                onMouseLeave={() => { setHoveredButton(null); if (pressedButton === 'login-mobile') setPressedButton(null); }}
                onMouseDown={() => setPressedButton('login-mobile')}
                onMouseUp={() => setPressedButton(null)}
                onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
              >
                Log In
              </button>
              <button
                style={{
                  ...styles.signupBtn,
                  ...styles.signupBtnMobile,
                  ...styles.mobileMenuAuthButton,
                  ...(hoveredButton === 'signup-mobile' ? styles.signupBtnHover : {}),
                  ...(pressedButton === 'signup-mobile' ? styles.btnPressed : {})
                }}
                onMouseEnter={() => setHoveredButton('signup-mobile')}
                onMouseLeave={() => { setHoveredButton(null); if (pressedButton === 'signup-mobile') setPressedButton(null); }}
                onMouseDown={() => setPressedButton('signup-mobile')}
                onMouseUp={() => setPressedButton(null)}
                onClick={() => { setMobileMenuOpen(false); navigate('/signup'); }}
              >
                Sign Up
              </button>
            </div>
          </div>
        </>
      )}
      <main className="lp-main" style={styles.main}>
        <section className="lp-hero" style={{
          ...styles.heroSection,
          ...(isMobile ? styles.heroSectionMobile : {})
        }}>
          <div className="lp-hero-content" style={{
            ...styles.heroContent,
            ...(pageLoaded ? styles.fadeInUp : styles.fadeStart)
          }}>
            <h1 className="lp-hero-title" style={styles.heroTitle}>Welcome to Believers World</h1>
            <p className="lp-hero-sub" style={styles.heroSubline}>Connect with Jesus lovers around the globe</p>
            <p className="lp-hero-desc" style={styles.heroDesc}>Belivah is a joyful Christian community offering edifying content and wholesome entertainment in a safe, moral environment.</p>
            <button
              style={{
                ...styles.ctaButton,
                ...(hoveredButton === 'cta1' ? styles.ctaButtonHover : {}),
                ...(pressedButton === 'cta1' ? styles.btnPressed : {})
              }}
              onMouseEnter={() => setHoveredButton('cta1')}
              onMouseLeave={() => { setHoveredButton(null); if (pressedButton === 'cta1') setPressedButton(null); }}
              onMouseDown={() => setPressedButton('cta1')}
              onMouseUp={() => setPressedButton(null)}
              onClick={() => navigate('/signup')}
              className="lp-cta"
            >
              Join Our Community
            </button>
            <button type="button" className="lp-cta-secondary" onClick={() => navigate('/login')}>
              I already have an account
            </button>
          </div>
          <div className="lp-hero-img-wrap" style={{
            ...styles.heroImageWrap,
            ...(pageLoaded ? styles.fadeInUpDelayed : styles.fadeStart)
          }}>
            <img
              onMouseEnter={() => setHeroHover(true)}
              onMouseLeave={() => setHeroHover(false)}
              src="belivah-image.png"
              alt="Diverse global community"
              style={{
                ...styles.heroImage,
                ...(heroHover ? styles.heroImageHover : {})
              }}
            />
          </div>
        </section>
        <section className="lp-features" style={styles.featuresSection} id="features">
          <h2 className="lp-section-title" style={styles.sectionTitle}>Discover Amazing Features</h2>
          <p className="lp-section-sub" style={styles.sectionSubtitle}>Experience a social platform designed specifically for believers. Here's what makes Belivah special:</p>
          
          <div className="lp-feature-grid" style={styles.featureGrid}>
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 0 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(0)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 0 ? styles.featureIconHover : {})
              }}>🎬</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Pulse Videos</h3>
              <p className="lp-feature-text" style={styles.featureText}>Share uplifting short-form videos that inspire faith, spread joy, and encourage spiritual growth. From worship moments to motivational messages, Pulse is where faith meets creativity in bite-sized content.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 1 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(1)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 1 ? styles.featureIconHover : {})
              }}>📖</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Testimony Wall</h3>
              <p className="lp-feature-text" style={styles.featureText}>Share your personal faith journey and witness God's work in your life. Read powerful testimonies from believers worldwide that will strengthen your faith and remind you that you're never alone in your walk with Christ.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 2 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(2)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 2 ? styles.featureIconHover : {})
              }}>👥</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Community Groups</h3>
              <p className="lp-feature-text" style={styles.featureText}>Join vibrant communities centered around Bible study, prayer, worship, youth ministry, and more. Connect with believers who share your interests, grow together in faith, and build lasting friendships in a supportive environment.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 3 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(3)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 3 ? styles.featureIconHover : {})
              }}>💬</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Direct Messaging</h3>
              <p className="lp-feature-text" style={styles.featureText}>Build meaningful one-on-one connections with fellow believers. Share prayer requests, offer encouragement, and develop authentic relationships in a private, secure messaging environment.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 4 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(4)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 4 ? styles.featureIconHover : {})
              }}>🛡️</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Safe & Pure Environment</h3>
              <p className="lp-feature-text" style={styles.featureText}>Enjoy social media without compromise. Our advanced content moderation, profanity filters, and dedicated moderation team ensure a wholesome space free from inappropriate content, so you can engage without worry.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 5 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(5)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 5 ? styles.featureIconHover : {})
              }}>✝️</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Faith-Centered Content</h3>
              <p className="lp-feature-text" style={styles.featureText}>Every post, video, and message is designed to uplift, inspire, and glorify God. Discover daily devotionals, worship content, biblical discussions, and inspiring stories that strengthen your spiritual walk.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 6 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(6)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 6 ? styles.featureIconHover : {})
              }}>🌍</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Global Community</h3>
              <p className="lp-feature-text" style={styles.featureText}>Connect with Christians from every corner of the world. Experience diverse expressions of faith, learn from different cultures, and witness how God is moving across nations—all in one unified platform.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 7 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(7)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 7 ? styles.featureIconHover : {})
              }}>💃</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Clean Entertainment</h3>
              <p className="lp-feature-text" style={styles.featureText}>Enjoy wholesome entertainment that honors God! Discover uplifting music, clean comedy, inspiring dramas, and fun content that brings joy without compromise. Laugh, relax, and be entertained in a space free from profanity and worldly influences.</p>
            </div>
            
            <div
              className="lp-feature-card"
              style={{
                ...styles.featureCard,
                ...(hoveredFeature === 8 ? styles.featureCardHover : {})
              }}
              onMouseEnter={() => setHoveredFeature(8)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              <div className="lp-feature-icon" style={{
                ...styles.featureIcon,
                ...(hoveredFeature === 8 ? styles.featureIconHover : {})
              }}>🎯</div>
              <h3 className="lp-feature-title" style={styles.featureCardTitle}>Purpose-Driven Platform</h3>
              <p className="lp-feature-text" style={styles.featureText}>Unlike mainstream social media, Belivah balances spiritual growth with wholesome entertainment. Connect, be inspired, and enjoy yourself—all while staying true to your faith values in a community that understands you.</p>
            </div>
          </div>
          
          <div className="lp-cta-box" style={styles.ctaBox}>
            <h3 style={styles.ctaBoxTitle}>Ready to Experience the Difference?</h3>
            <p style={styles.ctaBoxText}>Join thousands of believers who have found their online home at Belivah.</p>
            <button
              style={{
                ...styles.ctaButton,
                ...(hoveredButton === 'cta2' ? styles.ctaButtonHover : {}),
                ...(pressedButton === 'cta2' ? styles.btnPressed : {})
              }}
              onMouseEnter={() => setHoveredButton('cta2')}
              onMouseLeave={() => { setHoveredButton(null); if (pressedButton === 'cta2') setPressedButton(null); }}
              onMouseDown={() => setPressedButton('cta2')}
              onMouseUp={() => setPressedButton(null)}
              onClick={() => navigate('/signup')}
            >
              Create Free Account
            </button>
          </div>
        </section>

        <section style={styles.aboutSection} id="about">
          <h2 className="lp-section-title" style={styles.sectionTitle}>About Belivah</h2>
          <div className="lp-about-card" style={styles.aboutContent}>
            <div style={styles.aboutText}>
              <h3 style={styles.aboutSubheading}>Our Story</h3>
              <p style={styles.paragraph}>
                Belivah was born from a deep concern shared by young believers around the world. As Christians navigating the digital age, we found ourselves constantly bombarded with content that contradicts our values. Mainstream social media platforms have become filled with profanity, immorality, and distractions that pull us away from our faith.
              </p>
              <p style={styles.paragraph}>
                We watched as fellow believers struggled to stay connected online while maintaining their spiritual integrity. Many Christians want to enjoy social media, be entertained, watch good content, and have fun—but not at the expense of their values. The choice seemed impossible: either engage with mainstream platforms and compromise your values, or disconnect entirely and miss out on entertainment and meaningful connections.
              </p>
              <p style={styles.paragraph}>
                <strong>That's why we created Belivah.</strong>
              </p>
              <p style={styles.paragraph}>
                Our team consists of young, passionate believers who understand the unique challenges Christians face in today's digital landscape. We're not just developers and designers—we're brothers and sisters in Christ who believe you deserve a space where you can laugh, be entertained, connect with others, and grow spiritually—all without encountering profanity or immoral content.
              </p>
              
              <h3 style={styles.aboutSubheading}>Our Mission</h3>
              <p style={styles.paragraph}>
                Belivah exists to provide a morally pure digital community where believers can be entertained, inspired, and connected without compromising their faith. We're not solely spiritual—we celebrate good music, clean comedy, uplifting dramas, and wholesome fun. We believe entertainment and faith can coexist beautifully when done right.
              </p>
              <p style={styles.paragraph}>
                You shouldn't have to choose between enjoying yourself online and maintaining your spiritual standards. At Belivah, you get both—entertainment that honors God and content that uplifts your spirit.
              </p>
              <p style={styles.paragraph}>
                Every feature, every policy, and every decision we make is guided by biblical principles and a commitment to creating the safest, most uplifting social media experience possible.
              </p>
              
              <h3 style={styles.aboutSubheading}>What Makes Us Different</h3>
              <ul style={styles.list}>
                <li style={styles.listItem}><strong>Zero Tolerance for Profanity:</strong> Our advanced filters and moderation team ensure clean, wholesome content—always.</li>
                <li style={styles.listItem}><strong>Faith-First Community:</strong> Every member shares your values and commitment to Christ.</li>
                <li style={styles.listItem}><strong>Clean Entertainment:</strong> Enjoy music, comedy, drama, and fun content without worldly influences or immorality.</li>
                <li style={styles.listItem}><strong>No Compromise:</strong> Entertainment and inspiration that honors God—no inappropriate ads, no immoral content.</li>
                <li style={styles.listItem}><strong>Built by Believers:</strong> Created and maintained by Christians who understand your desire for both entertainment and spiritual growth.</li>
                <li style={styles.listItem}><strong>Safe for All Ages:</strong> A platform where families can laugh, connect, and be entertained together without worry.</li>
              </ul>
              
              <p style={styles.paragraph}>
                We're not perfect, but we're committed to creating a space where Christ is honored, believers are encouraged, and everyone can have fun, laugh, and be entertained—all without fear of encountering profanity or content that grieves the Holy Spirit. Belivah is where faith meets joy, where worship meets laughter, and where spiritual growth meets wholesome entertainment.
              </p>
              
              <p style={styles.paragraphHighlight}>
                "Pure and undefiled religion before God and the Father is this: to visit orphans and widows in their trouble, <span style={styles.scriptureEmphasis}>and to keep oneself unspotted from the world.</span>" - James 1:27
              </p>
            </div>
          </div>
        </section>

        <section className="lp-contact" style={styles.contactSection} id="contact">
          <h2 className="lp-section-title" style={styles.sectionTitle}>Contact Us</h2>
          <p className="lp-section-sub" style={styles.sectionSubtitle}>Have questions, feedback, or need support? We'd love to hear from you!</p>
          
          <div style={styles.contactContent}>
            <div style={styles.contactInfo}>
              <h3 style={styles.contactInfoTitle}>Get in Touch</h3>
              <div style={styles.contactInfoItem}>
                <span style={styles.contactIcon}>📧</span>
                <div>
                  <strong>Email us directly:</strong>
                  <br />
                  <a href="mailto:info@belivah.com" style={styles.emailLink}>info@belivah.com</a>
                </div>
              </div>
              <p style={styles.contactInfoText}>
                We typically respond within 24-48 hours. For urgent matters, please mark your message as "Urgent" in the subject line.
              </p>
              <div style={styles.contactInfoItem}>
                <span style={styles.contactIcon}>⏰</span>
                <div>
                  <strong>Response Time:</strong>
                  <br />
                  Monday - Friday: Within 24 hours
                  <br />
                  Weekends: Within 48 hours
                </div>
              </div>
            </div>
            
            <div style={styles.contactFormContainer}>
              <h3 style={styles.contactFormTitle}>Send Us a Message</h3>
              {submitMessage && (
                <div style={{
                  ...styles.submitMessage,
                  background: submitMessage.includes('Thank you') ? '#d4edda' : '#f8d7da',
                  color: submitMessage.includes('Thank you') ? '#155724' : '#721c24',
                }}>
                  {submitMessage}
                </div>
              )}
              <form onSubmit={handleContactSubmit} style={styles.contactForm}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Your Name *</label>
                  <input
                    type="text"
                    required
                    style={{
                      ...styles.formInput,
                      ...(focusedField === 'name' ? styles.formInputFocus : {})
                    }}
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="John Doe"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Your Email *</label>
                  <input
                    type="email"
                    required
                    style={{
                      ...styles.formInput,
                      ...(focusedField === 'email' ? styles.formInputFocus : {})
                    }}
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="john@example.com"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Subject *</label>
                  <input
                    type="text"
                    required
                    style={{
                      ...styles.formInput,
                      ...(focusedField === 'subject' ? styles.formInputFocus : {})
                    }}
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                    onFocus={() => setFocusedField('subject')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="How can we help you?"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Message *</label>
                  <textarea
                    required
                    style={{
                      ...styles.formInput,
                      ...styles.formTextarea,
                      ...(focusedField === 'message' ? styles.formInputFocus : {})
                    }}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Please describe your question, feedback, or concern..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    ...styles.submitButton,
                    ...(hoveredButton === 'submit' ? styles.ctaButtonHover : {}),
                    ...(pressedButton === 'submit' ? styles.btnPressed : {}),
                    opacity: submitting ? 0.6 : 1,
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={() => setHoveredButton('submit')}
                  onMouseLeave={() => setHoveredButton(null)}
                  onMouseDown={() => setPressedButton('submit')}
                  onMouseUp={() => setPressedButton(null)}
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </section>

        <footer style={styles.footer}>
          <div style={styles.footerContent}>
            <div style={styles.footerBrand}>
              <h3 style={styles.footerLogo}>Belivah</h3>
              <p style={styles.footerTagline}>INSPIRE. ENTERTAIN. CONNECT.</p>
            </div>
            <div style={styles.footerLinks}>
              <a href="#features" style={styles.footerLink}>Features</a>
              <a href="#about" style={styles.footerLink}>About</a>
              <a href="#contact" style={styles.footerLink}>Contact</a>
            </div>
            <div style={styles.footerCopyright}>
              <p style={styles.copyrightText}>© {currentYear} Belivah. All rights reserved.</p>
              <p style={styles.verseText}>"Keep oneself unspotted from the world" - James 1:27</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    minHeight: '100vh',
    background: '#F5EDE1',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  navbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px 48px',
    background: '#0B6B6F',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  navbarMobile: {
    padding: '16px 20px',
  },
  logo: {
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '2px',
    display: 'flex',
    flexDirection: 'column',
  },
  tagline: {
    fontSize: '0.85rem',
    fontWeight: 400,
    color: '#66EAD1',
    marginTop: '2px',
    letterSpacing: '1px',
  },
  navLinks: {
    display: 'flex',
    gap: '32px',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginLeft: 'auto',
  },
  linkWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
  },
  linkWrapMobile: {
    width: '100%',
    padding: '8px 0',
  },
  link: {
    color: '#fff',
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'color 0.2s',
    cursor: 'pointer',
  },
  linkMobile: {
    fontSize: '1.1rem',
    fontWeight: 600,
  },
  linkUnderline: {
    height: '2px',
    background: '#66EAD1',
    borderRadius: '2px',
    transition: 'width 200ms ease',
    width: '0%',
  },
  authButtons: {
    display: 'flex',
    gap: '16px',
  },
  loginBtn: {
    background: 'transparent',
    color: '#fff',
    border: '2px solid #66EAD1',
    borderRadius: '8px',
    padding: '10px 28px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s, transform 0.2s, box-shadow 0.2s',
  },
  loginBtnMobile: {
    width: '100%',
    padding: '14px',
    fontSize: '1rem',
    borderWidth: '2px',
  },
  loginBtnHover: {
    background: '#66EAD1',
    color: '#072B2C',
    boxShadow: '0 6px 16px rgba(102,234,209,0.3)',
    transform: 'translateY(-2px)',
  },
  signupBtn: {
    background: '#66EAD1',
    color: '#072B2C',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 28px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s, transform 0.2s, box-shadow 0.2s',
  },
  signupBtnMobile: {
    width: '100%',
    padding: '14px',
    fontSize: '1rem',
  },
  signupBtnHover: {
    background: 'linear-gradient(135deg, #66EAD1 0%, #17A2A9 100%)',
    color: '#072B2C',
    boxShadow: '0 8px 18px rgba(23,162,169,0.3)',
    transform: 'translateY(-2px)',
  },
  mobileMenuButton: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.7)',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    transition: 'background 0.2s, border-color 0.2s',
  },
  mobileMenuButtonActive: {
    background: 'rgba(255,255,255,0.1)',
    borderColor: '#66EAD1',
  },
  mobileMenuIconBar: {
    width: '20px',
    height: '2px',
    background: '#fff',
    borderRadius: '2px',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  },
  mobileMenuBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 999,
  },
  mobileMenuPanel: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '100%',
    maxWidth: '320px',
    background: '#0B6B6F',
    color: '#fff',
    height: '100vh',
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease',
    padding: '80px 24px 32px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  mobileMenuPanelOpen: {
    transform: 'translateX(0)',
  },
  mobileMenuLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  mobileMenuActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  mobileMenuAuthButton: {
    width: '100%',
  },
  btnPressed: {
    transform: 'scale(0.98)',
    boxShadow: 'none',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 24px',
  },
  heroSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '48px',
    marginBottom: '80px',
    flexWrap: 'wrap',
  },
  heroSectionMobile: {
    flexDirection: 'column-reverse',
    alignItems: 'flex-start',
    gap: '32px',
  },
  heroContent: {
    flex: 1,
    minWidth: '320px',
  },
  heroTitle: {
    fontSize: '2.8rem',
    fontWeight: 800,
    color: '#0B6B6F',
    marginBottom: '20px',
    lineHeight: 1.2,
  },
  heroSubline: {
    fontSize: '1.05rem',
    fontStyle: 'italic',
    color: '#17A2A9',
    marginTop: '-8px',
    marginBottom: '24px',
    letterSpacing: '0.5px',
  },
  heroDesc: {
    fontSize: '1.25rem',
    color: '#072B2C',
    marginBottom: '32px',
    lineHeight: 1.6,
  },
  ctaButton: {
    background: '#17A2A9',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '16px 40px',
    fontSize: '1.2rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(23,162,169,0.3)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  ctaButtonHover: {
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: '0 10px 24px rgba(23,162,169,0.4)',
    background: 'linear-gradient(135deg, #17A2A9 0%, #0B6B6F 100%)',
  },
  heroImageWrap: {
    flex: 1,
    minWidth: '320px',
    display: 'flex',
    justifyContent: 'center',
  },
  heroImage: {
    width: '100%',
    maxWidth: '500px',
    borderRadius: '24px',
    transition: 'transform 0.3s ease',
  },
  heroImageHover: {
    transform: 'scale(1.02)',
  },
  fadeStart: {
    opacity: 0,
    transform: 'translateY(12px)'
  },
  fadeInUp: {
    opacity: 1,
    transform: 'none',
    transition: 'opacity 600ms ease, transform 600ms cubic-bezier(0.2, 0.8, 0.2, 1)'
  },
  fadeInUpDelayed: {
    opacity: 1,
    transform: 'none',
    transition: 'opacity 700ms ease 80ms, transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1) 80ms'
  },
  featuresSection: {
    marginTop: '60px',
    marginBottom: '80px',
  },
  sectionTitle: {
    fontSize: '2.5rem',
    color: '#0B6B6F',
    fontWeight: 800,
    marginBottom: '16px',
    textAlign: 'center',
  },
  sectionSubtitle: {
    fontSize: '1.2rem',
    color: '#072B2C',
    textAlign: 'center',
    marginBottom: '48px',
    maxWidth: '700px',
    margin: '0 auto 48px',
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '32px',
    marginBottom: '48px',
  },
  featureCard: {
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(11,107,111,0.08)',
    padding: '32px 24px',
    textAlign: 'center',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  featureCardHover: {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 28px rgba(11,107,111,0.18)',
    border: '1px solid rgba(102, 234, 209, 0.5)'
  },
  featureIcon: {
    fontSize: '3rem',
    marginBottom: '16px',
    transition: 'transform 0.25s ease',
  },
  featureIconHover: {
    transform: 'scale(1.08)'
  },
  featureCardTitle: {
    fontSize: '1.4rem',
    color: '#17A2A9',
    fontWeight: 700,
    marginBottom: '12px',
  },
  featureText: {
    fontSize: '1rem',
    color: '#072B2C',
    lineHeight: 1.6,
  },
  ctaBox: {
    background: 'linear-gradient(135deg, #0B6B6F 0%, #17A2A9 100%)',
    borderRadius: '20px',
    padding: '48px 32px',
    textAlign: 'center',
    color: '#fff',
    marginTop: '48px',
  },
  ctaBoxTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: '16px',
  },
  ctaBoxText: {
    fontSize: '1.2rem',
    marginBottom: '28px',
    color: '#F5EDE1',
  },
  aboutSection: {
    marginTop: '80px',
    marginBottom: '80px',
  },
  aboutContent: {
    background: '#fff',
    borderRadius: '20px',
    padding: '48px',
    boxShadow: '0 4px 24px rgba(11,107,111,0.08)',
  },
  aboutText: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  aboutSubheading: {
    fontSize: '1.8rem',
    color: '#17A2A9',
    fontWeight: 700,
    marginTop: '32px',
    marginBottom: '16px',
  },
  paragraph: {
    fontSize: '1.1rem',
    color: '#072B2C',
    lineHeight: 1.8,
    marginBottom: '16px',
  },
  paragraphHighlight: {
    fontSize: '1.2rem',
    color: '#0B6B6F',
    fontStyle: 'italic',
    fontWeight: 600,
    textAlign: 'center',
    marginTop: '32px',
    padding: '24px',
    background: '#F5EDE1',
    borderRadius: '12px',
    borderLeft: '4px solid #66EAD1',
  },
  list: {
    fontSize: '1.1rem',
    color: '#072B2C',
    lineHeight: 1.8,
    marginLeft: '24px',
    marginBottom: '16px',
  },
  listItem: {
    marginBottom: '12px',
  },
  scriptureEmphasis: {
    fontWeight: 900,
    background: 'linear-gradient(135deg, #17A2A9 0%, #0B6B6F 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '0.2px',
  },
  contactSection: {
    marginTop: '80px',
    marginBottom: '60px',
  },
  contactContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '40px',
    marginTop: '40px',
  },
  contactInfo: {
    background: '#fff',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 16px rgba(11,107,111,0.08)',
  },
  contactInfoTitle: {
    fontSize: '1.6rem',
    color: '#0B6B6F',
    fontWeight: 700,
    marginBottom: '24px',
  },
  contactInfoItem: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    alignItems: 'flex-start',
  },
  contactIcon: {
    fontSize: '1.8rem',
  },
  emailLink: {
    color: '#17A2A9',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '1.1rem',
  },
  contactInfoText: {
    fontSize: '1rem',
    color: '#072B2C',
    lineHeight: 1.6,
    marginTop: '16px',
  },
  contactFormContainer: {
    background: '#fff',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 16px rgba(11,107,111,0.08)',
  },
  contactFormTitle: {
    fontSize: '1.6rem',
    color: '#0B6B6F',
    fontWeight: 700,
    marginBottom: '24px',
  },
  contactForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  formLabel: {
    fontSize: '1rem',
    color: '#072B2C',
    fontWeight: 600,
  },
  formInput: {
    padding: '12px 16px',
    fontSize: '1rem',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  },
  formInputFocus: {
    borderColor: '#17A2A9',
    boxShadow: '0 0 0 4px rgba(102, 234, 209, 0.25)',
    outline: 'none',
  },
  formTextarea: {
    minHeight: '120px',
    resize: 'vertical',
  },
  submitButton: {
    background: '#17A2A9',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 32px',
    fontSize: '1.1rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginTop: '8px',
  },
  submitMessage: {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '1rem',
    fontWeight: 500,
  },
  footer: {
    background: '#0B6B6F',
    color: '#fff',
    marginTop: '80px',
    padding: '48px 24px 32px',
    borderRadius: '20px 20px 0 0',
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
  },
  footerBrand: {
    textAlign: 'center',
  },
  footerLogo: {
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '2px',
    marginBottom: '8px',
  },
  footerTagline: {
    fontSize: '1rem',
    color: '#66EAD1',
    fontWeight: 500,
    letterSpacing: '1px',
  },
  footerLinks: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  footerLink: {
    color: '#F5EDE1',
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'color 0.2s',
  },
  footerCopyright: {
    textAlign: 'center',
    borderTop: '1px solid rgba(102, 234, 209, 0.3)',
    paddingTop: '24px',
    width: '100%',
  },
  copyrightText: {
    fontSize: '0.95rem',
    color: '#F5EDE1',
    marginBottom: '8px',
  },
  verseText: {
    fontSize: '0.9rem',
    color: '#66EAD1',
    fontStyle: 'italic',
  },
};
